import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  generateTextWithSearchGrounding,
  generateTextWithLite,
  suggestDesignColors,
  generateImage,
} from '@/services/geminiService';
import { useCustomDesign } from '@/contexts/CustomDesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Bot, User, Send, Sparkles, Palette, Package, Info, Download, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { GroundingChunk } from '@google/genai';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  sources?: { uri: string; title: string }[];
  type?: 'text' | 'colors' | 'products' | 'logo';
  data?: any;
  isGenerating?: boolean;
}

const BotAvatar: React.FC = () => (
  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
    <Bot className="w-5 h-5 text-white" />
  </div>
);

const UserAvatar: React.FC = () => (
  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
    <User className="w-5 h-5 text-gray-600" />
  </div>
);

const ChatBubble: React.FC<{ message: ChatMessage; onSaveLogo?: (imageUrl: string, prompt: string) => void; onDownloadLogo?: (imageUrl: string) => void }> = ({ message, onSaveLogo, onDownloadLogo }) => {
  const isUser = message.sender === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      {isUser ? <UserAvatar /> : <BotAvatar />}

      <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[80%]`}>
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-br-none'
              : 'bg-gray-100 text-gray-900 rounded-bl-none'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.text}</p>

          {/* Logo Generation */}
          {message.type === 'logo' && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              {message.isGenerating ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
                    <p className="text-xs font-semibold text-gray-600">Generating your logo...</p>
                  </div>
                </div>
              ) : message.data?.imageUrl ? (
                <div className="space-y-3">
                  <div className="relative bg-white rounded-lg p-4 shadow-inner">
                    <img
                      src={message.data.imageUrl}
                      alt="Generated Logo"
                      className="w-full h-auto max-h-64 object-contain rounded"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => onSaveLogo?.(message.data.imageUrl, message.data.prompt || '')}
                      variant="default"
                      size="sm"
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save Design
                    </Button>
                    <Button
                      onClick={() => onDownloadLogo?.(message.data.imageUrl)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-red-600">Failed to generate logo</p>
              )}
            </div>
          )}

          {/* Color Suggestions */}
          {message.type === 'colors' && message.data?.colors && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              <p className="text-xs font-semibold">Suggested Colors:</p>
              <div className="flex gap-2 flex-wrap">
                {message.data.colors.map((color: string, index: number) => (
                  <div
                    key={index}
                    className="flex flex-col items-center gap-1"
                  >
                    <div
                      className="w-10 h-10 rounded-lg border-2 border-white shadow-md"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs font-mono">{color}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources */}
          {message.sources && message.sources.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Sources:
              </p>
              <div className="space-y-1">
                {message.sources.map((source, index) => (
                  <a
                    key={index}
                    href={source.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs underline hover:text-purple-300 transition-colors"
                  >
                    {source.title || 'Link'}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <span className="text-xs text-muted-foreground mt-1 px-2">
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

const QuickActions: React.FC<{ onSelect: (prompt: string) => void }> = ({ onSelect }) => {
  const actions = [
    { icon: Sparkles, label: 'Generate Logo', prompt: 'Generate a modern minimalist logo for a coffee shop' },
    { icon: Palette, label: 'Color Schemes', prompt: 'Suggest color combinations for a modern hoodie design' },
    { icon: Package, label: 'Product Recommendations', prompt: 'What products work best for minimalist designs?' },
    { icon: Info, label: 'Printing Tips', prompt: 'What should I know about printing high-quality designs?' },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 p-4">
      {actions.map((action, index) => (
        <Button
          key={index}
          variant="outline"
          className="h-auto py-3 flex flex-col items-center gap-2 hover:border-purple-500 transition-colors"
          onClick={() => onSelect(action.prompt)}
        >
          <action.icon className="h-5 w-5 text-purple-600" />
          <span className="text-xs font-medium">{action.label}</span>
        </Button>
      ))}
    </div>
  );
};

export const DesignAssistant: React.FC = () => {
  const { addDesign } = useCustomDesign();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      text: "Hi! I'm your AI Design Assistant. I can help you with:\n\n• Generate custom logos with AI\n• Creative design ideas and inspiration\n• Color palette suggestions\n• Product recommendations\n• Printing tips and best practices\n\nWhat would you like to create today?",
      sender: 'bot',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = useCallback(
    async (messageText?: string) => {
      const text = messageText || input.trim();
      if (!text || isLoading) return;

      const userMessage: ChatMessage = {
        id: `user_${Date.now()}`,
        text,
        sender: 'user',
      };

      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const lowercasedInput = text.toLowerCase();

        // Logo generation handler
        if (
          (lowercasedInput.includes('generate') || lowercasedInput.includes('create') || lowercasedInput.includes('make')) &&
          lowercasedInput.includes('logo')
        ) {
          // Create placeholder message with loading state
          const loadingMessageId = `bot_${Date.now()}`;
          const loadingMessage: ChatMessage = {
            id: loadingMessageId,
            text: "I'll generate that logo for you right now!",
            sender: 'bot',
            type: 'logo',
            isGenerating: true,
          };
          setMessages((prev) => [...prev, loadingMessage]);

          try {
            // Generate the logo
            const imageUrl = await generateImage(text, '1:1');

            // Update the message with the generated logo
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === loadingMessageId
                  ? {
                      ...msg,
                      text: "Here's your custom logo! You can save it to your designs or download it directly.",
                      isGenerating: false,
                      data: { imageUrl, prompt: text },
                    }
                  : msg
              )
            );
            toast.success('Logo generated successfully!');
          } catch (error) {
            console.error('Logo generation error:', error);
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === loadingMessageId
                  ? {
                      ...msg,
                      text: "I couldn't generate the logo. Please make sure your API key is configured and try again.",
                      isGenerating: false,
                      data: null,
                    }
                  : msg
              )
            );
            toast.error('Failed to generate logo');
          }
          return;
        }

        // Color suggestion handler
        if (lowercasedInput.includes('color') && (lowercasedInput.includes('suggest') || lowercasedInput.includes('palette') || lowercasedInput.includes('combination'))) {
          const colors = await suggestDesignColors('general product', text);
          const botMessage: ChatMessage = {
            id: `bot_${Date.now()}`,
            text: "Here are some color combinations that would work great for your design:",
            sender: 'bot',
            type: 'colors',
            data: { colors },
          };
          setMessages((prev) => [...prev, botMessage]);
          return;
        }

        // Search grounding for latest trends
        let response;
        if (
          lowercasedInput.includes('latest') ||
          lowercasedInput.includes('trend') ||
          lowercasedInput.includes('popular')
        ) {
          response = await generateTextWithSearchGrounding(text);
        } else {
          response = await generateTextWithLite(text);
        }

        // Extract sources if available
        let sources: { uri: string; title: string }[] = [];
        const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
        if (groundingChunks) {
          sources = groundingChunks
            .map((chunk: GroundingChunk) => {
              const sourceData = chunk.web;
              return {
                uri: sourceData?.uri || '#',
                title: sourceData?.title || 'Untitled Source',
              };
            })
            .filter((source) => source.uri !== '#');
        }

        const botMessage: ChatMessage = {
          id: `bot_${Date.now()}`,
          text: response.text.trim(),
          sender: 'bot',
          sources: sources.length > 0 ? sources : undefined,
        };

        setMessages((prev) => [...prev, botMessage]);
      } catch (error) {
        console.error('Chat error:', error);
        const errorMessage: ChatMessage = {
          id: `bot_${Date.now()}`,
          text: "I'm having trouble connecting right now. Please make sure your API key is configured and try again.",
          sender: 'bot',
        };
        setMessages((prev) => [...prev, errorMessage]);
        toast.error('Failed to get response');
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading]
  );

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSaveLogo = useCallback((imageUrl: string, prompt: string) => {
    try {
      addDesign({
        imageUrl,
        prompt,
        productId: 'logo',
      });
      toast.success('Logo saved to your designs!');
    } catch (error) {
      console.error('Save logo error:', error);
      toast.error('Failed to save logo');
    }
  }, [addDesign]);

  const handleDownloadLogo = useCallback((imageUrl: string) => {
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `logo-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Logo downloaded!');
    } catch (error) {
      console.error('Download logo error:', error);
      toast.error('Failed to download logo');
    }
  }, []);

  return (
    <div className="container mx-auto p-6">
      <Card className="max-w-4xl mx-auto shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">AI Design Assistant</CardTitle>
              <CardDescription>
                Generate logos, get design advice, and explore creative ideas
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Messages Area */}
          <ScrollArea className="h-[500px] p-6" ref={scrollAreaRef}>
            {messages.length === 1 && (
              <div className="mb-6">
                <ChatBubble
                  message={messages[0]}
                  onSaveLogo={handleSaveLogo}
                  onDownloadLogo={handleDownloadLogo}
                />
                <div className="mt-6">
                  <p className="text-sm font-medium text-center mb-3 text-muted-foreground">
                    Quick Actions
                  </p>
                  <QuickActions onSelect={handleSendMessage} />
                </div>
              </div>
            )}

            {messages.length > 1 &&
              messages.map((msg) => (
                <ChatBubble
                  key={msg.id}
                  message={msg}
                  onSaveLogo={handleSaveLogo}
                  onDownloadLogo={handleDownloadLogo}
                />
              ))}

            {isLoading && (
              <div className="flex gap-3 mb-4">
                <BotAvatar />
                <div className="bg-gray-100 rounded-2xl rounded-bl-none px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t p-4 bg-gray-50">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <Input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about design..."
                className="flex-1"
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                size="icon"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Powered by Google Gemini AI
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};